import { useQuery } from "convex/react";
import { useMemo, ReactElement } from "react";
import type { FunctionReference } from "convex/server";
import { getColumnCount } from "./useTableColums";
import SkeletonTable from "@/components/table/skeleton-table";
import { TableColumn } from "@/components/table/data-table";
import { isOffline } from "@/lib/offline/is-offline";
import { useStableCompanyId } from "@/hooks/offline/useStableCompanyId";
import { useTableReplica } from "@/hooks/offline/useTableReplica";
import { queryCachedRows, replicaScopeOf } from "@/lib/offline/table-offline-query";
import { useCollectionReplica } from "@/hooks/localfirst/useCollectionReplica";
import { queryLocalRecords } from "@/lib/localfirst/records";

export interface TableSearchParams {
  search?: string;
  status?: string;
  page?: number;
  [key: string]: any;
}

export interface TableConfig<TData = any> {
  tableName: string;
  query: FunctionReference<"query">;
  title: string;
  description: string;
  defaultPageSize?: number;
  columns?: TableColumn[]; // Columnas para el skeleton
  buildQueryParams?: (
    currentPage: number,
    pageSize: number,
    searchParams: TableSearchParams,
  ) => Record<string, any>;
  buildPageUrl?: (
    basePath: string,
    page: number,
    searchParams: TableSearchParams,
  ) => string;
}

export interface TableDataResult<TData = any> {
  data: TData[] | undefined;
  totalCount: number;
  /** totalCount es una cota inferior (paginación por índice, ver index_paginate). */
  totalCountIsLowerBound: boolean;
  isLoading: boolean;
  isError: boolean;
  /** Sin conexión y sin datos del servidor: mostrar aviso offline por vista. */
  isOfflineNoData: boolean;
  /** Sirviendo filas desde la réplica local (sin conexión). */
  servedFromCache: boolean;
  /** Offline con datos en pantalla (réplica o memoria de Convex): mostrar ícono. */
  isOfflineViewing: boolean;
  /** La réplica local terminó de hidratar (haya o no filas). Sirve para que la
   *  tabla muestre filas locales y de réplica JUNTAS, sin el flash de dos pasos. */
  replicaHydrated: boolean;
  currentPage: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  basePath: string;
  nextPageUrl: string | null;
  previousPageUrl: string | null;
  resetFiltersUrl: string;
  renderSkeleton: () => ReactElement;
  renderError: (message?: string) => ReactElement;
  navigationProps: {
    basePath: string;
    currentPage: number;
    pageSize: number;
    totalCount: number;
    isLoading: boolean;
    nextPageUrl: string | null;
    previousPageUrl: string | null;
    resetFiltersUrl: string;
  };
}

export function useTableData<TData = any>(
  config: TableConfig<TData>,
  options: {
    currentPage: number;
    pageSize?: number;
    basePath: string;
    searchParams?: TableSearchParams;
    /**
     * Args extra merged encima del resultado de `buildQueryParams`
     * antes de invocar la query de Convex. La key "collectionId"
     * gana si también viene del `buildQueryParams` (caso típico
     * del factory de collection-records).
     */
    extraArgs?: Record<string, any>;
  },
): TableDataResult<TData> {
  const {
    tableName,
    query,
    title,
    description,
    defaultPageSize = 20,
    columns,
    buildQueryParams,
    buildPageUrl,
  } = config;

  const {
    currentPage,
    pageSize = defaultPageSize,
    basePath,
    searchParams = {},
    extraArgs,
  } = options;

  const queryParams = useMemo(() => {
    const baseParams = (() => {
      if (buildQueryParams) {
        return buildQueryParams(currentPage, pageSize, searchParams);
      }

      // Filtrar parametros de UI que no son parte del validador de Convex
      const UI_PARAMS = new Set(["action", "targetMaxRows"]);
      const { page, ...restParams } = searchParams;
      const validSearchParams = Object.fromEntries(
        Object.entries(restParams).filter(([key]) => !UI_PARAMS.has(key)),
      );

      return {
        pageNumber: (currentPage || 1) - 1, // 0-indexed para paginateAndSort
        targetMaxRows:
          searchParams.targetMaxRows !== undefined
            ? typeof searchParams.targetMaxRows === "string"
              ? Number(searchParams.targetMaxRows.replace(/^"|"$/g, "")) ||
                pageSize
              : searchParams.targetMaxRows
            : pageSize,
        includeTotalCount: true,
        search: searchParams.search,
        status: searchParams.status,
        ...validSearchParams,
      };
    })();
    // `extraArgs` del componente que monta el factory van siempre
    // encima del resultado de `buildQueryParams` (o del default).
    // Patrón típico: el componente concreto pasa `valueFilters`
    // calculado en su propio render sin reorganizar el factory.
    return { ...baseParams, ...(extraArgs ?? {}) };
  }, [currentPage, pageSize, searchParams, buildQueryParams, extraArgs]);

  // Colección «Disponible sin conexión»: la tabla se arma sobre la réplica
  // local-first (también con red) y no se pide la página al servidor.
  const replica = useCollectionReplica(
    tableName === "collection_records" && typeof queryParams.collectionId === "string"
      ? queryParams.collectionId
      : null,
  );
  const queryResult = useQuery(query, replica.active ? "skip" : queryParams);

  const buildUrl = useMemo(() => {
    if (buildPageUrl) {
      return (page: number) => buildPageUrl(basePath, page, searchParams);
    }

    return (page: number) => {
      const params = new URLSearchParams();
      if (searchParams.search) params.set("search", searchParams.search);
      if (searchParams.status) params.set("status", searchParams.status);

      Object.entries(searchParams).forEach(([key, value]) => {
        if (
          key !== "search" &&
          key !== "status" &&
          key !== "page" &&
          value !== undefined
        ) {
          params.set(key, String(value));
        }
      });

      // Incluir targetMaxRows si existe en searchParams
      if (searchParams.targetMaxRows !== undefined) {
        params.set("targetMaxRows", String(searchParams.targetMaxRows));
      }

      params.set("page", String(page));
      return `${basePath}?${params.toString()}`;
    };
  }, [basePath, searchParams, buildPageUrl]);

  const rawData = queryResult?.page as TData[] | undefined;
  const rawTotalCount = queryResult?.totalCount ?? 0;

  // Calcular pageSize actual basado en searchParams.targetMaxRows
  const currentPageSize =
    searchParams.targetMaxRows !== undefined
      ? typeof searchParams.targetMaxRows === "string"
        ? Number(searchParams.targetMaxRows.replace(/^"|"$/g, "")) || pageSize
        : searchParams.targetMaxRows
      : pageSize;

  // ─── Réplica de lectura offline ───────────────────────────────────────────
  // Online: se espejan las filas que trae la query. Offline (query undefined) y
  // con filas cacheadas: se sirven con filtrado/orden/paginación client-side, así
  // la tabla muestra datos en vez del aviso "sin conexión". Ver table-replica-db.
  const companyId = useStableCompanyId();
  const { cachedRows, isHydrated: replicaHydrated } = useTableReplica<
    TData & { _id: string }
  >({
    tableName: replicaScopeOf(tableName, queryParams),
    liveRows: rawData as Array<TData & { _id: string }> | undefined,
    companyId,
  });
  const servedFromCache =
    isOffline() && queryResult === undefined && cachedRows.length > 0;
  const cachedResult = useMemo(
    () =>
      servedFromCache
        ? queryCachedRows(cachedRows as Array<Record<string, unknown>>, {
            search: searchParams.search,
            status: searchParams.status,
            numericRanges: searchParams.numericRanges as string | undefined,
            page: currentPage || 1,
            pageSize: currentPageSize,
          })
        : null,
    [
      servedFromCache,
      cachedRows,
      searchParams.search,
      searchParams.status,
      searchParams.numericRanges,
      currentPage,
      currentPageSize,
    ],
  );

  const localResult = useMemo(
    () =>
      replica.active
        ? queryLocalRecords(replica.records, {
            search: searchParams.search,
            valueFilters: queryParams.valueFilters as string | undefined,
            sortBy: queryParams.sortBy as string | undefined,
            sortDir: queryParams.sortDir as "asc" | "desc" | undefined,
            page: currentPage || 1,
            pageSize: currentPageSize,
          })
        : null,
    [
      replica.active,
      replica.records,
      searchParams.search,
      queryParams.valueFilters,
      queryParams.sortBy,
      queryParams.sortDir,
      currentPage,
      currentPageSize,
    ],
  );

  const data = localResult
    ? (localResult.page as unknown as TData[])
    : servedFromCache
      ? (cachedResult!.page as unknown as TData[])
      : rawData;
  const totalCount = localResult
    ? localResult.total
    : servedFromCache
      ? cachedResult!.totalCount
      : rawTotalCount;
  const totalCountIsLowerBound =
    !localResult && !servedFromCache && queryResult?.totalCountIsLowerBound === true;

  const totalPages = Math.ceil(totalCount / currentPageSize);

  const hasNextPage = currentPage < totalPages;
  const hasPreviousPage = currentPage > 1;

  const nextPageUrl = hasNextPage ? buildUrl(currentPage + 1) : null;
  const previousPageUrl = hasPreviousPage ? buildUrl(currentPage - 1) : null;

  // Construir resetFiltersUrl con targetMaxRows si existe
  const resetFiltersUrl = useMemo(() => {
    const params = new URLSearchParams();
    if (searchParams.targetMaxRows !== undefined) {
      params.set("targetMaxRows", String(searchParams.targetMaxRows));
    }
    params.set("page", "1");
    return `${basePath}?${params.toString()}`;
  }, [basePath, searchParams.targetMaxRows]);

  // Cargando (skeleton) mientras la query no resolvió y no estamos sirviendo
  // cache. Offline, mientras la réplica se hidrata, esto sigue true → skeleton
  // (no el aviso), que es lo que evita el flash.
  const isLoading = replica.active
    ? replica.hydrating && !isOffline()
    : queryResult === undefined && !servedFromCache;
  const isError = queryResult === null;

  // Aviso offline por vista SOLO cuando no hay red, la query no resolvió, la
  // réplica YA se hidrató y no hay nada cacheado. Gatear por `replicaHydrated`
  // evita el flash del aviso antes de que carguen los datos locales: mientras
  // hidrata se muestra el skeleton (isLoading), no el aviso.
  const isOfflineNoData = replica.active
    ? isOffline() && replica.hydrating
    : isOffline() &&
      queryResult === undefined &&
      replicaHydrated &&
      cachedRows.length === 0;

  // Indicador de "viendo offline": sin conexión y con datos en pantalla, ya sea
  // desde la réplica local (página no cargada) o desde la copia en memoria de
  // Convex (página que ya estaba suscripta al caer la red). Ambos casos son
  // datos cacheados/posiblemente desactualizados → mostramos el ícono siempre.
  const isOfflineViewing =
    isOffline() && !isLoading && !isOfflineNoData && (data?.length ?? 0) > 0;

  const renderSkeleton = () => {
    const columnCount = getColumnCount(tableName as any) || 8;

    return (
      <SkeletonTable
        columsNumber={columnCount}
        rowsNumber={pageSize}
        title={title}
        description={description}
        columns={columns}
      />
    );
  };

  const renderError = (message?: string) => (
    <div className="p-6 text-center">
      <p className="text-muted-foreground">
        {message || `No se pudieron cargar los datos`}
      </p>
    </div>
  );

  const navigationProps = {
    basePath,
    currentPage,
    pageSize: currentPageSize,
    totalCount,
    isLoading,
    nextPageUrl,
    previousPageUrl,
    resetFiltersUrl,
  };

  return {
    data,
    totalCount,
    totalCountIsLowerBound,
    isLoading,
    isError,
    isOfflineNoData,
    servedFromCache,
    isOfflineViewing,
    replicaHydrated,
    currentPage,
    pageSize: currentPageSize,
    totalPages,
    hasNextPage,
    hasPreviousPage,
    basePath,
    nextPageUrl,
    previousPageUrl,
    resetFiltersUrl,
    renderSkeleton,
    renderError,
    navigationProps,
  };
}
